import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";

export const COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH = 200;

export enum CompanyBookmarkOrderField {
  DateBookmarked = "DateBookmarked",
  Name = "Name",
}

const DEFAULT_ORDER_FIELD = CompanyBookmarkOrderField.DateBookmarked;
const DEFAULT_ORDER_DIRECTION = OrderDirection.Descending;

/**
 * The column filters. `name` keeps the companies whose name contains it. The dates keep the
 * bookmarks made at or after `bookmarkedFrom` and before `bookmarkedBefore`: instants, so the table
 * turns the days the reader picks into the start of each day where the reader is.
 */
export type CompanyBookmarkFilters = {
  name?: string;
  bookmarkedFrom?: Date;
  bookmarkedBefore?: Date;
};

type CompanyBookmarkQueryValues = {
  paginationParameters?: PaginationParameters;
  orderBy?: CompanyBookmarkOrderField;
  orderDir?: OrderDirection;
  query?: string;
  filters?: CompanyBookmarkFilters;
};

function isValidDate(value: Date | undefined): boolean {
  return value === undefined || (value instanceof Date && !Number.isNaN(value.getTime()));
}

/**
 * The arguments of `GET marketplace/company-bookmarks`: one page of the caller's company
 * bookmarks, searched, filtered and ordered by the server. The default is the newest bookmark first.
 *
 * A name order with no direction is ascending (A to Z). The server's default direction belongs to
 * the default field only, so the search params leave out the order only when it is that default
 * (`DateBookmarked`, `Descending`), and always send the direction of any other field.
 */
export default class CompanyBookmarkQuery {
  public readonly paginationParameters: PaginationParameters;
  public readonly orderBy: CompanyBookmarkOrderField;
  public readonly orderDir: OrderDirection;
  public readonly query?: string;
  public readonly filters: Readonly<CompanyBookmarkFilters>;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.cacheParams]);
  }

  /** Every argument except the page, so the pages of one search share one key. */
  public get cacheKey(): string {
    return new URLSearchParams(this.cacheParams).toString();
  }

  public constructor(values: CompanyBookmarkQueryValues = {}) {
    const paginationParameters = values.paginationParameters ?? PaginationParameters.DEFAULT;
    if (
      !(paginationParameters instanceof PaginationParameters) ||
      !Number.isInteger(paginationParameters.pageIndex) || paginationParameters.pageIndex < 0 ||
      !Number.isInteger(paginationParameters.pageSize) || paginationParameters.pageSize < 1 ||
      paginationParameters.pageSize > 100
    ) {
      throw new Error("Valid pagination parameters are required.");
    }
    if (values.orderBy && !Object.values(CompanyBookmarkOrderField).includes(values.orderBy)) {
      throw new Error("A valid company bookmark order field is required.");
    }
    if (values.orderDir && !Object.values(OrderDirection).includes(values.orderDir)) {
      throw new Error("A valid order direction is required.");
    }
    const query = values.query?.trim();
    if (query && query.length > COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH) {
      throw new Error(`query cannot exceed ${COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH} characters.`);
    }
    const name = values.filters?.name?.trim();
    if (name && name.length > COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH) {
      throw new Error(`The name filter cannot exceed ${COMPANY_BOOKMARK_QUERY_MAXIMUM_LENGTH} characters.`);
    }
    const { bookmarkedFrom, bookmarkedBefore } = values.filters ?? {};
    if (!isValidDate(bookmarkedFrom) || !isValidDate(bookmarkedBefore)) {
      throw new Error("The date filters must be valid dates.");
    }
    if (bookmarkedFrom && bookmarkedBefore && bookmarkedFrom >= bookmarkedBefore) {
      throw new Error("The end of the date range must be after its start.");
    }

    const orderBy = values.orderBy ?? DEFAULT_ORDER_FIELD;
    this.paginationParameters = paginationParameters;
    this.orderBy = orderBy;
    this.orderDir = values.orderDir ?? (orderBy === DEFAULT_ORDER_FIELD ? DEFAULT_ORDER_DIRECTION : OrderDirection.Ascending);
    this.query = query || undefined;
    this.filters = {
      name: name || undefined,
      bookmarkedFrom,
      bookmarkedBefore,
    };
  }

  public withPagination(paginationParameters: PaginationParameters): CompanyBookmarkQuery {
    return new CompanyBookmarkQuery({
      paginationParameters,
      orderBy: this.orderBy,
      orderDir: this.orderDir,
      query: this.query,
      filters: this.filters,
    });
  }

  public isEqualTo(other?: CompanyBookmarkQuery): boolean {
    if (!other) return false;
    return this.cacheKey === other.cacheKey && this.paginationParameters.isEqualTo(other.paginationParameters);
  }

  private get cacheParams(): string[][] {
    const isDefaultField = this.orderBy === DEFAULT_ORDER_FIELD;
    const isDefaultOrder = isDefaultField && this.orderDir === DEFAULT_ORDER_DIRECTION;
    return [
      ...(isDefaultField ? [] : [["orderBy", this.orderBy]]),
      ...(isDefaultOrder ? [] : [["orderDir", this.orderDir]]),
      ...(this.query ? [["query", this.query]] : []),
      ...(this.filters.name ? [["name", this.filters.name]] : []),
      ...(this.filters.bookmarkedFrom ? [["bookmarkedFrom", this.filters.bookmarkedFrom.toISOString()]] : []),
      ...(this.filters.bookmarkedBefore ? [["bookmarkedBefore", this.filters.bookmarkedBefore.toISOString()]] : []),
    ];
  }
}
